import {booleanAttribute, ChangeDetectionStrategy, Component, input, signal} from "@angular/core";
import {EnkelBrukerModell, GruppeMedlemListeModell, HttpsUrl, LitenIkkeTomStreng} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initialer} from "felles-ng2/støttefunksjoner/strenger";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatTooltipModule} from "@angular/material/tooltip";
import {NgOptimizedImage} from "@angular/common";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {primærMedlemsrolle} from "felles-ng2/modell/medlemModellHjelpere";
import {RessursAdresser} from "felles-ng2/tjenester/ressurser/RessursAdresser";
import {toObservable} from "@angular/core/rxjs-interop";
import {combineLatest} from "rxjs";

const debug = initDebugger('AvatarKomponent');

export type avatarStil = 'mini' | 'liten' | 'stor' | 'xstor' | 'xxstor' | '';

@Component({
    selector: 'avatar',
    templateUrl: 'avatar.html',
    styleUrl: 'avatar.scss',
    host: {
        '[class]': 'stil()',
        '[attr.e2e-avatar-initialer]': 'initialer()',
        'data-testid': 'avatar'
    },
    imports: [
        MatTooltipModule,
        NgOptimizedImage
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvatarKomponent {

    bildeurl = input<HttpsUrl | null>(null);
    bruker = input<EnkelBrukerModell | null>(null);
    medlem = input<GruppeMedlemListeModell | null>(null);
    navn = input<string | null>(null);

    skjulTooltip = input(false, {transform: booleanAttribute});
    stil = input<avatarStil>('');


    url = signal<string | null>(null);
    initialer = signal<string | null>(null);
    imgAltTekst = signal('avatar');
    tooltiptekst = signal('');

    ikon = 'ion-ios-person';

    constructor(private oversettelser: Oversettelser, private ressursAdresser: RessursAdresser) {
        combineLatest({
            bildeurl: toObservable(this.bildeurl),
            bruker: toObservable(this.bruker),
            medlem: toObservable(this.medlem),
            navn: toObservable(this.navn)
        }).subscribe(argumenter => this.beregnVerdier(argumenter));
    }

    // private hjelpemetoder

    private beregnVerdier(argumenter: {
        bildeurl: HttpsUrl | null;
        bruker: EnkelBrukerModell | null;
        medlem: GruppeMedlemListeModell | null;
        navn: LitenIkkeTomStreng | null
    }) {
        //debug(`beregn basert på %j`, argumenter)

        if (erIkkeNil(argumenter.bildeurl)) {
            this.url.set(argumenter.bildeurl);
            this.tooltiptekst.set('');
            this.imgAltTekst.set('avatar');

        } else if (erIkkeNil(argumenter.bruker?.avatarId)) {
            this.url.set(this.ressursAdresser.brukerAvatarUrl(argumenter.bruker!));
            this.tooltiptekst.set(argumenter.bruker.navnEllerEpost);
            this.imgAltTekst.set(argumenter.bruker.navnEllerEpost);
            this.initialer.set(erIkkeNil(argumenter.bruker.navn) ? initialer(argumenter.bruker.navn) : null);

        } else if (erIkkeNil(argumenter.medlem?.avatarId)) {
            this.url.set(this.ressursAdresser.medlemAvatarUrl(argumenter.medlem!));
            const tilgangsstatus = argumenter.medlem?.harTilgang ? '' : this.oversettelser.oversett(i18n('(tilgang stengt)'));
            this.tooltiptekst.set(`${argumenter.medlem.navn}, ${primærMedlemsrolle(argumenter.medlem)?.tittel ?? ''} ${tilgangsstatus}`);
            this.imgAltTekst.set(argumenter.medlem.navn);
            this.initialer.set(erIkkeNil(argumenter.medlem.navn) ? initialer(argumenter.medlem.navn) : null);

        } else if (erIkkeNil(argumenter.bruker?.navn)) {
            this.url.set(null);
            this.tooltiptekst.set(argumenter.bruker.navn);
            this.imgAltTekst.set(argumenter.bruker!.navn);
            this.initialer.set(initialer(argumenter.bruker.navn));

        } else if (erIkkeNil(argumenter.medlem?.navn)) {
            this.url.set(null);
            this.tooltiptekst.set(argumenter.medlem.navn);
            this.imgAltTekst.set(argumenter.medlem!.navn);
            this.initialer.set(initialer(argumenter.medlem.navn));

        } else if (erIkkeNil(argumenter.navn)) {
            this.url.set(null);
            this.tooltiptekst.set(argumenter.navn);
            this.imgAltTekst.set(argumenter.navn);
            this.initialer.set(initialer(argumenter.navn));
        }
    }

}
